import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Inbox,
  Loader2,
  Replace,
  Search,
  X,
} from "lucide-react";
import { PrimaryButton } from "@/components/PrimaryButton";
import { useApp, type Card } from "@/context/AppContext";
import {
  completePickList,
  fetchPickList,
  initPickList,
  patchPickRow,
  type ApiPickRow,
} from "@/context/catalogSync";

export const Route = createFileRoute("/sales/$id/pick")({
  component: PickPage,
});

function PickPage() {
  const { id: saleId } = Route.useParams();
  const navigate = useNavigate();
  const { sales, products, cards, storageBins } = useApp();

  const sale = useMemo(
    () => sales.find((s) => s.id === saleId),
    [sales, saleId],
  );
  const product = useMemo(
    () => (sale ? products.find((p) => p.id === sale.itemId) : undefined),
    [products, sale],
  );

  const [rows, setRows] = useState<ApiPickRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [banner, setBanner] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [substituteFor, setSubstituteFor] = useState<ApiPickRow | null>(null);

  useEffect(() => {
    if (!saleId) return;
    let cancelled = false;
    setLoading(true);
    fetchPickList(saleId)
      .then(async (rs) => {
        // Auto-init if no rows yet — saves an extra click for sales coming
        // straight from Mark Sold.
        if (rs.length === 0) {
          const initialized = await initPickList(saleId);
          if (!cancelled) setRows(initialized);
        } else {
          if (!cancelled) setRows(rs);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setBanner({
            kind: "error",
            text: err instanceof Error ? err.message : "Failed to load picks",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  const cardById = useMemo(() => {
    const map = new Map<string, Card>();
    for (const c of cards) map.set(c.id, c);
    return map;
  }, [cards]);

  const stats = useMemo(() => {
    if (!rows) return { total: 0, picked: 0 };
    return {
      total: rows.length,
      picked: rows.filter((r) => r.pickedCardId).length,
    };
  }, [rows]);
  const allPicked = stats.total > 0 && stats.picked === stats.total;

  // TNK-103 Phase G.3: group pick rows by the original card's storageLocation
  // so the operator can walk one bin at a time. Substituted rows stay under
  // their original bin (they're already picked anyway), and rows without a
  // bin assignment collapse into a single "No bin" group rendered last.
  const groupedRows = useMemo(() => {
    if (!rows) return [] as { bin: string; rows: ApiPickRow[] }[];
    const byBin = new Map<string, ApiPickRow[]>();
    for (const row of rows) {
      const card = cardById.get(row.originalCardId);
      const bin = card?.storageLocation?.trim() ?? "";
      const arr = byBin.get(bin) ?? [];
      arr.push(row);
      byBin.set(bin, arr);
    }
    const named = [...byBin.entries()]
      .filter(([bin]) => bin !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bin, rows]) => ({ bin, rows }));
    const unassigned = byBin.get("") ?? [];
    if (unassigned.length > 0) {
      named.push({ bin: "", rows: unassigned });
    }
    return named;
  }, [rows, cardById]);

  const binByName = useMemo(() => {
    const map = new Map<string, { color: string }>();
    for (const b of storageBins) map.set(b.name, { color: b.color });
    return map;
  }, [storageBins]);

  const onTogglePick = async (row: ApiPickRow) => {
    if (!rows) return;
    const currentlyPicked = !!row.pickedCardId;
    try {
      const updated = await patchPickRow(row.id, {
        pickedCardId: currentlyPicked ? null : row.originalCardId,
      });
      setRows(rows.map((r) => (r.id === row.id ? updated : r)));
    } catch (err) {
      setBanner({
        kind: "error",
        text: err instanceof Error ? err.message : "Pick failed",
      });
    }
  };

  const onChooseSubstitute = async (row: ApiPickRow, newCardId: string) => {
    if (!rows) return;
    try {
      const updated = await patchPickRow(row.id, {
        pickedCardId: newCardId,
      });
      setRows(rows.map((r) => (r.id === row.id ? updated : r)));
      setSubstituteFor(null);
      setBanner({
        kind: "success",
        text: "Substitution applied.",
      });
    } catch (err) {
      setBanner({
        kind: "error",
        text: err instanceof Error ? err.message : "Substitution failed",
      });
    }
  };

  const onComplete = async () => {
    if (!sale) return;
    setCompleting(true);
    try {
      await completePickList(sale.id);
      void navigate({ to: "/sales/$id", params: { id: sale.id } });
    } catch (err) {
      setBanner({
        kind: "error",
        text: err instanceof Error ? err.message : "Couldn't complete pick",
      });
    } finally {
      setCompleting(false);
    }
  };

  if (!sale) {
    return (
      <section className="flex h-full flex-col p-4">
        <p className="text-sm text-muted-foreground">Sale not found.</p>
      </section>
    );
  }

  return (
    <section className="flex h-full flex-col">
      <header className="space-y-2 border-b border-border pb-3">
        <button
          type="button"
          onClick={() =>
            void navigate({ to: "/sales/$id", params: { id: sale.id } })
          }
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to sale
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">
            Pick: {sale.itemName}
          </h1>
          <div className="text-xs text-muted-foreground">
            {stats.picked}/{stats.total} located · {sale.venue}
          </div>
        </div>
      </header>

      {banner && (
        <div
          className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
            banner.kind === "success"
              ? "border-emerald-600 bg-emerald-100 text-emerald-700"
              : "border-destructive bg-destructive/10 text-destructive"
          }`}
        >
          {banner.kind === "success" ? (
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          )}
          <div className="flex-1">{banner.text}</div>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            className="text-inherit hover:opacity-70"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading pick list…
        </div>
      ) : !rows || rows.length === 0 ? (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          No cards in this pick list. The product may be empty — return to the
          sale and mark it packed.
        </div>
      ) : (
        <div className="mt-3 flex-1 space-y-4 overflow-y-auto pb-24">
          {groupedRows.map(({ bin, rows: binRows }) => {
            const pickedHere = binRows.filter((r) => r.pickedCardId).length;
            const color = binByName.get(bin)?.color;
            return (
              <div key={bin || "_unassigned"} className="space-y-2">
                <div className="flex items-center gap-2 px-1">
                  {bin ? (
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: color ?? "var(--muted-foreground)",
                      }}
                    />
                  ) : (
                    <Inbox className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    {bin || "No bin"}
                  </h2>
                  <div className="text-[10px] text-muted-foreground">
                    {pickedHere}/{binRows.length} picked
                  </div>
                </div>
                <div className="space-y-2">
                  {binRows.map((row) => (
                    <PickRow
                      key={row.id}
                      row={row}
                      originalCard={cardById.get(row.originalCardId)}
                      substitutedCard={
                        row.pickedCardId &&
                        row.pickedCardId !== row.originalCardId
                          ? cardById.get(row.pickedCardId)
                          : undefined
                      }
                      onToggle={() => void onTogglePick(row)}
                      onOpenSubstitute={() => setSubstituteFor(row)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="sticky bottom-0 -mx-4 border-t border-border bg-background px-4 py-3">
        <PrimaryButton
          label={
            completing
              ? "Completing…"
              : allPicked
                ? "Complete Pick"
                : `Complete (${stats.picked}/${stats.total})`
          }
          onClick={() => void onComplete()}
          disabled={!allPicked || completing}
          leadingIcon={<CheckCircle2 className="h-4 w-4" />}
        />
      </div>

      {substituteFor && (
        <SubstituteDialog
          row={substituteFor}
          originalCard={cardById.get(substituteFor.originalCardId)}
          cards={cards}
          productId={product?.id ?? substituteFor.productId}
          onClose={() => setSubstituteFor(null)}
          onChoose={(cardId) => void onChooseSubstitute(substituteFor, cardId)}
        />
      )}
    </section>
  );
}

function PickRow({
  row,
  originalCard,
  substitutedCard,
  onToggle,
  onOpenSubstitute,
}: {
  row: ApiPickRow;
  originalCard?: Card;
  substitutedCard?: Card;
  onToggle: () => void;
  onOpenSubstitute: () => void;
}) {
  const isPicked = !!row.pickedCardId;
  const isSubstituted =
    row.pickedCardId !== undefined && row.pickedCardId !== row.originalCardId;
  const displayCard = substitutedCard ?? originalCard;
  return (
    <div
      className={`flex items-center gap-3 rounded-md border p-3 ${
        isPicked
          ? "border-emerald-300 bg-emerald-50/40"
          : "border-border bg-card"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={isPicked ? "Mark pending" : "Mark picked"}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border ${
          isPicked
            ? "border-emerald-600 bg-emerald-600 text-white"
            : "border-border bg-background"
        }`}
      >
        {isPicked && <CheckCircle2 className="h-4 w-4" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">
          {displayCard?.name ?? row.originalCardId}
        </div>
        <div className="truncate text-[11px] text-muted-foreground">
          {displayCard
            ? `${displayCard.setCode || displayCard.setName} · ${displayCard.cardNumber || "—"} · ${displayCard.condition}`
            : "(card not in inventory)"}
          {displayCard?.storageLocation
            ? ` · ${displayCard.storageLocation}`
            : ""}
        </div>
        {isSubstituted && originalCard && (
          <div className="mt-0.5 text-[10px] text-amber-700">
            Substituted (orig: {originalCard.name})
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenSubstitute}
        className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] hover:border-primary/50"
      >
        <Replace className="h-3.5 w-3.5" />
        Substitute
      </button>
    </div>
  );
}

function SubstituteDialog({
  row,
  originalCard,
  cards,
  productId,
  onClose,
  onChoose,
}: {
  row: ApiPickRow;
  originalCard?: Card;
  cards: Card[];
  productId: string;
  onClose: () => void;
  onChoose: (cardId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Best-effort filter: same setCode + cardNumber + condition first; then
    // widen to setCode + cardNumber; then any matching the search. Excludes
    // cards already reserved in OTHER products.
    const available = cards.filter(
      (c) =>
        c.id !== row.originalCardId &&
        (!c.packagedProductId || c.packagedProductId === productId),
    );
    const tiered = [
      ...available.filter(
        (c) =>
          originalCard &&
          c.setCode === originalCard.setCode &&
          c.cardNumber === originalCard.cardNumber &&
          c.condition === originalCard.condition,
      ),
      ...available.filter(
        (c) =>
          originalCard &&
          c.setCode === originalCard.setCode &&
          c.cardNumber === originalCard.cardNumber &&
          c.condition !== originalCard.condition,
      ),
    ];
    if (q) {
      return cards.filter(
        (c) =>
          c.id !== row.originalCardId &&
          (c.name.toLowerCase().includes(q) ||
            c.setCode.toLowerCase().includes(q) ||
            c.cardNumber.toLowerCase().includes(q)),
      );
    }
    return tiered.slice(0, 30);
  }, [cards, query, originalCard, row.originalCardId, productId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-md overflow-hidden rounded-t-lg bg-background shadow-lg sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">
              Substitute for {originalCard?.name ?? row.originalCardId}
            </div>
            {originalCard && (
              <div className="text-[11px] text-muted-foreground">
                {originalCard.setCode} {originalCard.cardNumber} ·{" "}
                {originalCard.condition}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-border p-2">
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, set, or number"
              className="h-9 w-full bg-transparent text-sm outline-none"
            />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {matches.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              No matching cards in inventory.
            </div>
          ) : (
            matches.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onChoose(c.id)}
                className="flex w-full items-center gap-3 border-b border-border/50 px-3 py-2 text-left hover:bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {c.name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {c.setCode} {c.cardNumber} · {c.condition}
                    {c.storageLocation ? ` · ${c.storageLocation}` : ""}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
